import { Suspense } from "react";
import { Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowRight } from "lucide-react";
import { listRelatedArticles } from "../lib/blog.functions";

function RelatedList({ topic }: { topic: string }) {
  const fetchRelated = useServerFn(listRelatedArticles);
  const { data } = useSuspenseQuery({
    queryKey: ["blog-related", topic],
    queryFn: () => fetchRelated({ data: { topic } }),
    staleTime: 60 * 60 * 1000,
  });

  if (!data || data.length === 0) return null;

  return (
    <section className="bg-[var(--brand-light-gray)] py-12 border-t border-gray-200">
      <div className="mx-auto max-w-[1200px] px-4">
        <h2
          className="text-[var(--brand-deep-blue)] text-xl md:text-2xl uppercase mb-5"
          style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
        >
          From Our Blog
        </h2>
        <ul className="grid gap-3 md:grid-cols-3">
          {data.map((a) => (
            <li key={a.slug}>
              <Link
                to="/blog/$slug"
                params={{ slug: a.slug }}
                className="flex h-full items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4 transition hover:border-[var(--brand-red)] hover:shadow-md"
              >
                <span className="text-sm font-semibold leading-snug text-[var(--brand-deep-blue)]">
                  {a.title}
                </span>
                <ArrowRight
                  size={16}
                  className="mt-0.5 shrink-0 text-[var(--brand-red)]"
                  aria-hidden="true"
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Internal-link strip to related blog posts. Renders nothing while loading. */
export function BlogRelatedStrip({ topic }: { topic: string }) {
  return (
    <Suspense fallback={null}>
      <RelatedList topic={topic} />
    </Suspense>
  );
}
